import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;
const RAZORPAY_TEST_KEY_ID = 'rzp_test_Q6kAjAT2Fg2Fxe'; // Your Test Key ID

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 600px;
  margin: 40px auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  font-family: 'Arial', sans-serif;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.8rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #555;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
  }
`;

const Button = styled.button`
  padding: 0.9rem 1.5rem;
  background-color: #28a745; /* Green for payment */
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: #218838;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ResponseArea = styled.pre`
  background-color: #f8f9fa;
  padding: 1rem;
  border: 1px solid #eee;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-top: 1.5rem;
  font-size: 0.85rem;
  max-height: 250px;
  overflow-y: auto;
`;

const ErrorMessage = styled.p`
  color: #dc3545; /* Red for errors */
  margin-top: 1rem;
  text-align: center;
`;

// Function to load Razorpay script dynamically
const loadRazorpayScript = (src: string) => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

const TestRazorpayPage: React.FC = () => {
  const [amount, setAmount] = useState<number>(100); // Default to 100 paisa = Re 1
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadRazorpayScript('https://checkout.razorpay.com/v1/checkout.js');
  }, []);

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setApiResponse(null);
    setError(null);
    axios.defaults.withCredentials = true; // If your API requires auth cookies

    try {
      // 1. Create Order on backend
      const orderResponse = await axios.post(`${API_BASE}/api/payment/create-order`, {
        amount: amount, // Amount in paisa
        currency: 'INR',
        receipt: `rcpt_test_${Date.now()}`,
      });

      if (!orderResponse.data.success || !orderResponse.data.order) {
        setError(orderResponse.data.message || 'Failed to create order.');
        setApiResponse(orderResponse.data);
        setIsLoading(false);
        return;
      }

      const { order } = orderResponse.data;
      setApiResponse({ step: 'Order Created', orderDetails: order });

      // 2. Open Razorpay Checkout
      const options = {
        key: RAZORPAY_TEST_KEY_ID,
        amount: order.amount, // Amount is in currency subunits. Default currency is INR. Hence, 50000 refers to 50000 paise
        currency: order.currency,
        name: 'Test Transaction MNGL',
        description: 'Test Payment for Razorpay Integration',
        image: 'https://example.com/your_logo.jpg', // Optional: your logo URL
        order_id: order.id, //This is a sample Order ID. Pass the `id` obtained in the response of Step 1
        handler: function (response: any) {
          setApiResponse({
            step: 'Payment Successful',
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            signature: response.razorpay_signature,
          });
          // TODO: Here you would typically verify the payment signature on your backend
          // For this test page, we'll just display the success.
          alert(`Payment Successful! Payment ID: ${response.razorpay_payment_id}`);
        },
        prefill: {
          // Optional: Prefill user details
          name: 'Test User',
          email: 'test.user@example.com',
          contact: '9999999999',
        },
        notes: {
          address: 'Test Corporate Office',
        },
        theme: {
          color: '#3399cc', // Theme color
        },
      };

      // @ts-ignore
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response: any) {
        setError(`Payment Failed: ${response.error.description} (Code: ${response.error.code})`);
        setApiResponse({
          step: 'Payment Failed',
          error: response.error,
        });
      });
      rzp.open();

    } catch (err: any) {
      console.error('Error during payment process:', err);
      if (axios.isAxiosError(err) && err.response) {
        setError(`Server Error: ${err.response.status} - ${err.response.data?.message || err.message}`);
        setApiResponse(err.response.data);
      } else {
        setError(`Request Error: ${err.message}`);
      }
    }
    setIsLoading(false);
  };

  return (
    <PageContainer>
      <Title>Test Razorpay Payment</Title>
      <Form onSubmit={handlePayment}>
        <div>
          <Label htmlFor="amount">Amount (in Paisa, e.g., 100 for ₹1):</Label>
          <Input
            id="amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            required
            min="100" // Minimum amount for Razorpay is usually Re 1
          />
        </div>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Processing...' : 'Create Order & Pay ₹' + (amount / 100).toFixed(2)}
        </Button>
      </Form>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {apiResponse && (
        <>
          <h3>API Interaction Log:</h3>
          <ResponseArea>{JSON.stringify(apiResponse, null, 2)}</ResponseArea>
        </>
      )}
    </PageContainer>
  );
};

export default TestRazorpayPage;
